import { useCallback, useEffect, useState } from "react";
import { Check, Copy, KeyRound, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, API } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

export function ApiKeyManager() {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [newKey, setNewKey] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/api-keys").then(({ data }) => setKeys(data)).catch(() => {});
  }, []);

  useEffect(load, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/api-keys", { name: name || "Untitled key" });
      setNewKey(data.key);
      setName("");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail, err.message));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/api-keys/${id}`);
      toast.success("Key revoked");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail, err.message));
    }
  };

  const copy = async (text, label) => {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const endpoint = `${API}/v1/events`;

  return (
    <div data-testid="api-key-manager">
      <form onSubmit={create} className="flex flex-wrap items-end gap-3" data-testid="api-key-form">
        <div className="min-w-[240px]">
          <Label className="text-xs uppercase tracking-wider text-neutral-400">Key name</Label>
          <Input
            data-testid="api-key-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Shopify site widget"
            className="mt-1.5 bg-white/5 border-white/10"
          />
        </div>
        <Button
          type="submit"
          disabled={busy}
          data-testid="create-api-key-btn"
          className="bg-primary text-black font-bold uppercase tracking-wider rounded-full hover:bg-[#FFA94D]"
        >
          <KeyRound className="h-4 w-4 mr-2" /> Generate key
        </Button>
      </form>

      {newKey && (
        <div className="mt-4 p-4 border border-primary/50 rounded-md bg-primary/5" data-testid="new-api-key-banner">
          <p className="text-xs uppercase tracking-wider text-primary">New key — copy it now, it won't be shown again</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <code className="font-mono text-[13px] break-all bg-black/50 px-3 py-2 rounded-sm" data-testid="new-api-key-value">
              {newKey}
            </code>
            <Button
              size="sm"
              variant="outline"
              data-testid="copy-api-key-btn"
              onClick={() => copy(newKey, "API key")}
              className="rounded-full border-white/20"
            >
              <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
            </Button>
            <Button
              size="sm"
              variant="ghost"
              data-testid="dismiss-api-key-btn"
              onClick={() => setNewKey("")}
              className="rounded-full text-neutral-400"
            >
              <Check className="h-3.5 w-3.5 mr-1.5" /> Saved it
            </Button>
          </div>
        </div>
      )}

      <div className="mt-6 space-y-2" data-testid="api-key-list">
        {keys.length === 0 && <p className="text-sm text-neutral-500">No API keys yet.</p>}
        {keys.map((k) => (
          <div key={k.id} className="flex flex-wrap items-center gap-3 p-3 border border-white/10 rounded-md" data-testid={`api-key-${k.id}`}>
            <div className="flex-1 min-w-[180px]">
              <div className="text-sm">
                {k.name}
                {k.revoked && <span className="ml-2 text-[10px] font-mono uppercase text-loss">Revoked</span>}
              </div>
              <div className="font-mono text-[11px] text-neutral-500">{k.masked}</div>
            </div>
            <div className="font-mono text-[11px] text-neutral-500">
              {k.request_count} calls{k.last_used_at ? ` · last ${new Date(k.last_used_at).toLocaleDateString("en-GB")}` : " · never used"}
            </div>
            {!k.revoked && (
              <Button
                size="icon"
                variant="ghost"
                data-testid={`revoke-api-key-${k.id}`}
                onClick={() => revoke(k.id)}
                className="text-neutral-400 hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
      </div>

      <div className="mt-8 border border-white/10 rounded-md p-4 bg-white/[0.02]">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-display">Endpoint</h3>
          <Button
            size="sm"
            variant="outline"
            data-testid="copy-endpoint-btn"
            onClick={() => copy(endpoint, "Endpoint URL")}
            className="rounded-full border-white/20"
          >
            <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy URL
          </Button>
        </div>
        <pre className="mt-3 font-mono text-[12px] text-neutral-300 whitespace-pre-wrap break-all">
{`GET ${endpoint}
Header: X-API-Key: <your key>

Optional: ?upcoming_only=false   (include past events)
          ?limit=50

Response:
{
  "count": 2,
  "events": [
    { "name": "Pokémon League Cup", "start": "2026-09-05T11:00:00",
      "price": "£20.00", "ticket_url": "https://..." }
  ]
}`}
        </pre>
        <p className="text-[11px] text-neutral-500 mt-3">
          Returns event name, date/time and ticket price only. Keys are stored hashed — nobody, including this
          dashboard, can read an existing key back.
        </p>
      </div>
    </div>
  );
}

export default ApiKeyManager;
